import React, { useEffect, useRef } from 'react';
import { TranslationKey, useI18n } from '../../../contexts/I18nContext';
import { TelemetrySession } from '../../../services/telemetryService';

/*
 * The calls of the day, one row each: when it started, when the line was
 * picked up and hung up, how long it lasted, how many DTMF were exchanged.
 * Clicking a row (or its bar in the chart) selects the call, and the DTMF
 * it exchanged are listed one by one below, with their exact time, the raw
 * frame and the event id.
 */

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string;

const OPENER_KEY: Record<string, TranslationKey> = {
  slic_ring: 'devices.monitoring.calls.slic_ring',
  slic_call: 'devices.monitoring.calls.slic_call',
  slic_connected: 'devices.monitoring.calls.slic_connected',
  slic_dialed: 'devices.monitoring.calls.slic_dialed',
  net_call: 'devices.monitoring.calls.net_call',
  line_connected: 'devices.monitoring.calls.line_connected',
  line_dialed: 'devices.monitoring.calls.line_dialed',
  line_alerted: 'devices.monitoring.calls.line_alerted',
  hang_up: 'devices.monitoring.calls.hang_up',
  dtmf: 'devices.monitoring.sessions.strayDtmf',
};

export const sessionTitle = (s: TelemetrySession, t: Translate): string =>
  s.opened_by && OPENER_KEY[s.opened_by] ? t(OPENER_KEY[s.opened_by]) : (s.opened_by ?? '–');

/** How the call ended, worded: who hung up, or that it timed out / is still going. */
export const sessionEnding = (s: TelemetrySession, t: Translate): string => {
  if (s.closed_by === 'hang_up') {
    if (s.hang_up_origin === '1') return t('devices.monitoring.sessions.hungUpByLine');
    if (s.hang_up_origin === '2') return t('devices.monitoring.sessions.hungUpByNetwork');
    return t('devices.monitoring.calls.hang_up');
  }
  if (s.closed_by === 'timeout') return t('devices.monitoring.sessions.noHangUp');
  return t('devices.monitoring.sessions.ongoing');
};

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null || !Number.isFinite(seconds)) return '–';
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, '0')} s`;
};

/**
 * When the line went off-hook: `slic_call` is the handset picked up to dial
 * (origin 1), `slic_connected` the line answering a network call.
 */
export const pickedUpAt = (s: TelemetrySession): string | null =>
  s.events.find((e) => e.evt === 'slic_call' || e.evt === 'slic_connected' || e.evt === 'line_connected')?.at ?? null;

/** When the call was hung up, if it was. */
export const hungUpAt = (s: TelemetrySession): string | null =>
  s.closed_by === 'hang_up' ? s.events.find((e) => e.evt === 'hang_up')?.at ?? s.end : null;

interface Props {
  sessions: TelemetrySession[];
  selectedStart: string | null;
  onSelect: (start: string) => void;
}

export const CallSessions: React.FC<Props> = ({ sessions, selectedStart, onSelect }) => {
  const { t, formatTime, formatDate } = useI18n();
  const clock = (iso: string | null) => (iso ? formatTime(iso, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '–');
  const stamp = (iso: string) => `${formatDate(iso)} ${clock(iso)}`;
  const selected = sessions.find((s) => s.start === selectedStart) ?? null;
  // The selected row stays in view when the selection comes from the chart.
  const selectedRow = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    selectedRow.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedStart]);

  return (
    <>
      <div className="monitoring-table-wrap calls-table-wrap">
        <table className="monitoring-table calls-table" data-testid="calls-table">
          <thead>
            <tr>
              <th>{t('devices.monitoring.sessions.columns.start')}</th>
              <th>{t('devices.monitoring.columns.event')}</th>
              <th>{t('devices.monitoring.columns.number')}</th>
              <th>{t('devices.monitoring.sessions.columns.pickedUp')}</th>
              <th>{t('devices.monitoring.sessions.columns.hungUp')}</th>
              <th className="numeric">{t('devices.monitoring.sessions.columns.duration')}</th>
              <th className="numeric">{t('devices.monitoring.sessions.columns.dtmf')}</th>
              <th>{t('devices.monitoring.sessions.columns.ending')}</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr
                key={s.start}
                ref={s.start === selectedStart ? selectedRow : undefined}
                className={`calls-row${s.start === selectedStart ? ' is-selected' : ''}`}
                onClick={() => onSelect(s.start)}
                aria-selected={s.start === selectedStart}
                data-testid="call-session"
              >
                <td className="monitoring-time">{stamp(s.start)}</td>
                <td>{sessionTitle(s, t)}</td>
                <td className="mono">{s.number ?? '–'}</td>
                <td className="monitoring-time">{clock(pickedUpAt(s))}</td>
                <td className="monitoring-time">{clock(hungUpAt(s))}</td>
                <td className="numeric">{formatDuration(s.duration_s)}</td>
                <td className="numeric">{s.dtmf_count > 0 ? <strong>{s.dtmf_count}</strong> : '0'}</td>
                <td>{sessionEnding(s, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <>
          <div className="monitoring-table-head">
            <span>{t('devices.monitoring.sessions.dtmfTitle', { time: stamp(selected.start), count: selected.dtmf.length })}</span>
            <span className="monitoring-table-hint">
              {t('devices.monitoring.sessions.dtmfCounts', {
                received: selected.dtmf.filter((d) => d.direction === 'I').length,
                sent: selected.dtmf.filter((d) => d.direction === 'O').length,
              })}
            </span>
          </div>
          {selected.dtmf.length === 0 ? (
            <p className="monitoring-empty">{t('devices.monitoring.sessions.noDtmf')}</p>
          ) : (
            <div className="monitoring-table-wrap" style={{ '--visible-rows': 12 } as React.CSSProperties}>
              <table className="monitoring-table" data-testid="dtmf-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('devices.monitoring.sessions.columns.at')}</th>
                    <th>{t('devices.monitoring.columns.key')}</th>
                    <th>{t('devices.monitoring.columns.direction')}</th>
                    <th>{t('devices.monitoring.columns.raw')}</th>
                    <th>{t('devices.monitoring.columns.eventId')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.dtmf.map((d, i) => (
                    <tr key={`${d.at}-${i}`}>
                      <td className="numeric">{i + 1}</td>
                      <td className="monitoring-time">{stamp(d.at)}</td>
                      <td className="mono"><strong>{d.key ?? '?'}</strong></td>
                      <td>{d.direction === 'I' ? t('devices.monitoring.dtmfReceived') : d.direction === 'O' ? t('devices.monitoring.dtmfSent') : '–'}</td>
                      <td className="mono">{d.digit ?? '–'}</td>
                      <td className="mono">{d.evt_id ?? '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
};
